import {
  ActionOutcomeSchema,
  BootstrapPrincipalRequestSchema,
  LayoutResponseSchema,
  AttendanceResponseSchema,
  ContainerSchema,
  TokenGrantSchema,
  IssuePreviewIdentityRequestSchema,
  PreviewIdentityAssertionSchema,
  PreviewIdentityConfigSchema,
  PreviewIdentityNonceResponseSchema,
  type Container,
  type Attendance,
  type TileLayout,
} from "@manifold/protocol";
import { requestJson } from "./http.ts";
import { ACCESS_CREATE_PRINCIPAL_ACTION, INDEX_READ_CONTAINER_ACTION } from "./assembly.ts";
import type { StoredIdentity } from "./identity-storage.ts";

function fieldFromObject(body: unknown, field: string): unknown {
  if (body === null || typeof body !== "object") {
    throw new Error("Server returned an invalid response");
  }
  return Reflect.get(body, field);
}

function authHeaders(token: string, includeJson: boolean): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    ...(includeJson ? { "Content-Type": "application/json" } : {}),
  };
}

/**
 * Exchanges the fragment-delivered owner key for a durable human token
 * (`core.access.createPrincipal`, named through `assembly.ts` like every door this floor
 * knocks on).
 *
 * The owner key is the ONE credential that lives outside the token system, which is why
 * this boot path holds a raw secret and no terminal: it authenticates as root, asks the
 * access door for an identity, and keeps only the grant. That fact used to be the argument
 * for `core.access` being ordinary; it is now the argument for why refusing to disable it is
 * SAFE (issue #113). The seat is `essential`, because this is the only door that turns a
 * credential into an identity and no browser without one can draw anything at all — and the
 * owner key remains the way back if an assembly arrives with the seat off out of band.
 */
export async function createPrincipal(
  ownerKey: string,
  input: { readonly name: string; readonly color: string },
): Promise<StoredIdentity> {
  const request = BootstrapPrincipalRequestSchema.parse({
    name: input.name,
    color: input.color,
    kind: "human",
  });
  const grant = TokenGrantSchema.parse(
    await dispatchAction(ownerKey, ACCESS_CREATE_PRINCIPAL_ACTION, request),
  );
  return {
    token: grant.token,
    principal: grant.principal,
    ...(grant.expiresAt === undefined ? {} : { expiresAt: grant.expiresAt }),
  };
}

/** Discovers whether this instance delegates browser admission to a production authority. */
export async function getPreviewIdentityAuthority(): Promise<string | null> {
  const body = await requestJson("/api/identity/preview-config", {});
  return PreviewIdentityConfigSchema.parse(body).authority;
}

/** Starts one browser-bound preview handoff and returns its short-lived nonce. */
export async function startPreviewIdentity(): Promise<string> {
  const body = await requestJson("/api/identity/preview-start", { method: "POST" });
  return PreviewIdentityNonceResponseSchema.parse(body).nonce;
}

/** Asks the signed-in production instance for one short-lived, audience-bound assertion. */
export async function issuePreviewIdentity(
  token: string,
  audience: string,
  nonce: string,
): Promise<string> {
  const input = IssuePreviewIdentityRequestSchema.parse({ audience, nonce });
  const body = await requestJson("/api/identity/preview-assertion", {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });
  return PreviewIdentityAssertionSchema.parse(body).assertion;
}

/**
 * Loads one container so a direct `/p/:id` deep-link still has its name and discipline
 * (`core.index.readContainer`). Declared `scope: "container"`, so a container-scoped viewer resolves its own
 * container exactly as `GET /api/containers/:id` let it.
 */
export async function getContainer(token: string, containerId: string): Promise<Container> {
  const result = await dispatchAction(token, INDEX_READ_CONTAINER_ACTION, { containerId });
  return ContainerSchema.parse(fieldFromObject(result, "container"));
}

/*
 * Renaming, deleting, machine listing and leaf removal were wrapped here too, and are not
 * any more: every one of them now belongs to a plugin that holds a `SessionClient`
 * (`renameContainer`, `deleteContainer`, `machines`, `removeContainerTile` on the SDK's own ref). What is
 * left in this layer is exactly what the BOOT path needs — a token, no terminal, one
 * container to name and one workspace tree to fetch — which is the whole reason it exists.
 */

/**
 * The CALLER's workspace tree (`GET /api/layout`) — the tile layout whose leaves name
 * plugin panels. Self-scoped by construction: the door takes no principal id and answers
 * the caller's own tree, falling back server-side to the engine's default workspace.
 */
export async function getWorkspaceLayout(token: string): Promise<TileLayout> {
  const body = await requestJson("/api/layout", {
    headers: authHeaders(token, false),
  });
  return LayoutResponseSchema.parse(body).layout;
}

/** Loads principal-level presence for containers with connected viewers. */
export async function getAttendance(token: string): Promise<readonly Attendance[]> {
  const body = await requestJson("/api/attendance", {
    headers: authHeaders(token, false),
  });
  return AttendanceResponseSchema.parse(body).attendance;
}

/*
 * Terminals had three bespoke routes wrapped here — the index, the rename and the kill. All
 * three are the action door now (`core.terminals.listAll` / `.rename` / `.kill`), reached
 * through `SessionClient` by every caller, so there is nothing left for this layer to wrap:
 * one door, and the shell reaches it exactly the way a plugin does.
 */

/**
 * One action dispatch over this device's token. The action door answers 200 for a REFUSAL
 * too, so the outcome decides and not the status; a denial becomes the thrown message every
 * other call in this layer already throws, because these callers render an error string and
 * have no rung to branch on. Code holding a `SessionClient` uses `client.action` instead —
 * this exists for the boot-time paths that hold a token and nothing else.
 */
export async function dispatchAction(token: string, name: string, args: unknown): Promise<unknown> {
  const body = await requestJson(`/api/actions/${encodeURIComponent(name)}`, {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(args),
  });
  const outcome = ActionOutcomeSchema.parse(body);
  if (!outcome.ok) throw new Error(outcome.denial.message);
  return outcome.result;
}
